import Add from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  getV1CompaniesCompanyIdSettlementsSettlementIdInvoices,
  patchV1CompaniesCompanyIdSettlementsSettlementIdInvoicesRebillRates,
  SettlementLineItemType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import Label from '~components/FormFields/v2/Label';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { SettlementLineItem } from '~hooks/useSettlementLineItems/models';
import useSettlementLineItems from '~hooks/useSettlementLineItems/useSettlementLineItems';
import { Settlement } from '~hooks/useSettlements/models';
import useSettlements from '~hooks/useSettlements/useSettlements';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { AddOnChargesDTO, AddOnChargesFormHandler } from '../AddOnChargesTableRows';
import { schemaHelpers } from '../helpers';
import RebillSettlementAddOnCharges from './RebillSettlement/RebillSettlementAddOnCharges';
import { RebillSettlementLineItems } from './RebillSettlement/RebillSettlementLineItems';
import { RebillSettlementPricing } from './RebillSettlement/RebillSettlementPricing';
import { COMMON_GRID_CONFIG } from './SettlementDetails/constants';

function LoadingPlaceholder() {
  return (
    <Box
      sx={{
        gridColumn: '1 / -1',
        display: 'grid',
        justifyContent: 'center',
        alignContent: 'center',
        height: '100px',
        bgcolor: 'background.paper',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

interface RebillSettlementFormProps {
  settlement?: Settlement;
  onRatesUpdated?: () => Promise<void>;
  onSubmit: () => void;
}

export default observer(function RebillSettlementForm({
  settlement,
  onSubmit,
  onRatesUpdated,
}: RebillSettlementFormProps) {
  const [lineItems, setLineItems] = useState<SettlementLineItem[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [newFreightRate, setNewFreightRate] = useState<string>('');
  const [isApplyingRate, setIsApplyingRate] = useState(false);
  const { settlementsStore, toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const addOnChargesRef = useRef<AddOnChargesFormHandler>({});

  const settlementId = settlement?.id ?? '';
  const settlementAddOnCharges = useMemo(() => {
    return settlementsStore.addOnChargesBySettlementId[settlementId] ?? [];
  }, [settlementId, settlementsStore.addOnChargesBySettlementId]);

  const formMethods = useForm<AddOnChargesDTO>({
    defaultValues: {
      addOnCharges: settlementAddOnCharges.map((addOnCharge) => {
        return schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge);
      }),
    },
    mode: 'all',
    delayError: 500,
  });
  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
    reset,
  } = formMethods;

  useEffect(() => {
    reset({
      addOnCharges: settlementAddOnCharges.map((addOnCharge) => {
        return schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge);
      }),
    });
  }, [settlementAddOnCharges, reset]);
  const watchedAddOnCharges = useWatch({ control, name: 'addOnCharges' });

  const {
    batchUpdateAddOnChargesOfSettlements,
    isUpdating: isUpdatingSettlementAddOnCharges,
  } = useAddOns();

  const headerColumns = useMemo(() => {
    return [
      t('approvals.driver_pay.settlements.pdf.date'),
      t('approvals.driver_pay.settlements.pdf.truck_num'),
      t('approvals.driver_pay.settlements.pdf.equipment_type'),
      t('approvals.driver_pay.settlements.pdf.driver'),
      t('approvals.driver_pay.settlements.pdf.vendor'),
      t('approvals.driver_pay.settlements.pdf.ticket'),
      t('approvals.driver_pay.settlements.pdf.material'),
      t('approvals.driver_pay.settlements.pdf.rate_type'),
      t('approvals.driver_pay.settlements.pdf.qty'),
      t('approvals.driver_pay.tabs.driver_pay.rate'),
      t('approvals.driver_pay.settlements.pdf.old_total'),
      t('approvals.driver_pay.settlements.pdf.new_total'),
      t('approvals.driver_pay.settlements.pdf.delta'),
    ];
  }, [settlement?.settlementLineItemType]);

  const gridConfig = useMemo(() => {
    const columns = headerColumns.length;
    const addonOffset = columns - 5;

    return {
      ...COMMON_GRID_CONFIG,
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gridTemplateAreas: `"${Array(addonOffset).fill('.').join(' ')} qty rate total rebillTotal delta"`,
    };
  }, [headerColumns.length]);

  const { getAllSettlementAddOnCharges } = useSettlements();
  const [isLoadingAllSettlementAddOnCharges, setIsLoadingAllSettlementAddOnCharges] =
    useState(false);
  const { getAllSettlementLineItems, isLoading: isLoadingAllSettlementLineItems } =
    useSettlementLineItems();
  const { currencyFormatter } = useCompanyCurrency();

  // Hydrate line items and add on charges on mount
  useEffect(() => {
    if (!settlementId) {
      return;
    }

    const getSettlementLineItems = async () => {
      try {
        const lineItemsRequest = await getAllSettlementLineItems(
          companyId,
          String(settlementId),
          SettlementLineItemType.LOAD,
        );
        const lineItems = lineItemsRequest.map((group) => {
          return group.lineItems;
        });
        setLineItems(lineItems.flat());
      } catch {
        // Error already handled in the hook.
      }
    };

    const getSettlementAddOnCharges = async () => {
      setIsLoadingAllSettlementAddOnCharges(true);
      try {
        await getAllSettlementAddOnCharges(companyId, String(settlementId));
      } catch {
        // Error already handled in the hook.
      } finally {
        setIsLoadingAllSettlementAddOnCharges(false);
      }
    };

    void getSettlementLineItems();
    void getSettlementAddOnCharges();
  }, [companyId, getAllSettlementAddOnCharges, getAllSettlementLineItems, settlementId]);

  const isLoadingAddOnArea = useMemo(
    () => isLoadingAllSettlementAddOnCharges || isLoadingAllSettlementLineItems,
    [isLoadingAllSettlementAddOnCharges, isLoadingAllSettlementLineItems],
  );

  const handleAddAddOnClick = useCallback(() => {
    addOnChargesRef.current.append?.();
    setIsEditing(true);
  }, [setIsEditing]);

  const isReadyForSubmission = useMemo(
    () => (watchedAddOnCharges?.length ?? 0) > 0 && isDirty && isValid,
    [isValid, isDirty, watchedAddOnCharges],
  );

  const canEditSettlement = useMemo(
    () => settlement?.editable ?? false,
    [settlement?.editable],
  );

  const onSubmitAddOns = useCallback(
    handleSubmit(async () => {
      const addOnCharges = (await addOnChargesRef.current.onSubmit?.()) ?? [];
      await batchUpdateAddOnChargesOfSettlements(companyId, settlementId, addOnCharges);
      setIsEditing(false);
      toasterStore.push(
        alert(
          t('approvals.driver_pay.settlements.add_ons_updated_successfully'),
          AlertTypes.success,
        ),
      );
      reset();

      // Notify parent component that settlement data needs to be refreshed
      await onRatesUpdated?.();
    }),
    [
      setIsEditing,
      reset,
      batchUpdateAddOnChargesOfSettlements,
      settlementId,
      toasterStore,
      companyId,
    ],
  );

  const handleApplyRate = useCallback(async () => {
    if (!newFreightRate || !settlementId) {
      toasterStore.push(
        alert(
          t('approvals.driver_pay.settlements.please_enter_valid_rate'),
          AlertTypes.error,
        ),
      );
      return;
    }

    const rateValue = parseFloat(newFreightRate);
    if (isNaN(rateValue) || rateValue < 0) {
      toasterStore.push(
        alert(
          t('approvals.driver_pay.settlements.please_enter_valid_rate'),
          AlertTypes.error,
        ),
      );
      return;
    }

    setIsApplyingRate(true);
    try {
      // Fetch all invoices for the settlement
      const invoicesResponse =
        await getV1CompaniesCompanyIdSettlementsSettlementIdInvoices({
          path: { 'company-id': companyId, 'settlement-id': settlementId },
        });

      const invoices = invoicesResponse.data.data;

      if (invoices.length === 0) {
        setIsApplyingRate(false);
        return;
      }

      // Prepare the request body with invoice IDs and the new rate
      const requestBody = {
        data: invoices.map((invoice) => ({
          invoice_id: invoice.id,
          rebill_rate_value: rateValue,
        })),
      };

      // Update the rates
      await patchV1CompaniesCompanyIdSettlementsSettlementIdInvoicesRebillRates({
        path: { 'company-id': companyId, 'settlement-id': settlementId },
        body: requestBody,
      });

      toasterStore.push(
        alert(
          t(
            'approvals.driver_pay.settlements.void_rebill_settlement.rate_applied_successfully',
          ),
          AlertTypes.success,
        ),
      );

      // Clear the input field
      setNewFreightRate('');

      // Refresh both line items and add-ons concurrently
      setIsLoadingAllSettlementAddOnCharges(true);
      const [lineItemsResult] = await Promise.allSettled([
        getAllSettlementLineItems(
          companyId,
          String(settlementId),
          SettlementLineItemType.LOAD,
        ),
        getAllSettlementAddOnCharges(companyId, String(settlementId)),
      ]);

      if (lineItemsResult.status === 'fulfilled') {
        const updatedLineItems = lineItemsResult.value.map((group) => {
          return group.lineItems;
        });
        setLineItems(updatedLineItems.flat());
      }

      // Notify parent component that rates have been updated
      await onRatesUpdated?.();
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.settlements.failed_to_apply_rate'),
      );
    } finally {
      setIsLoadingAllSettlementAddOnCharges(false);
      setIsApplyingRate(false);
    }
  }, [
    companyId,
    getAllSettlementAddOnCharges,
    getAllSettlementLineItems,
    newFreightRate,
    onRatesUpdated,
    settlementId,
    toasterStore,
  ]);

  const onSubmitMainForm = useCallback(
    handleSubmit(() => {
      onSubmit();
    }),
    [handleSubmit, onSubmit],
  );

  // This guard must stay below every hook: an early return above a hook makes
  // hook counts differ between renders once `settlement` toggles, crashing the
  // page with React errors 300/310 (HORIZON-FE-2RB).
  if (!settlement) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <form id="rebill-settlement-form" onSubmit={onSubmitMainForm}>
        <Box sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Label sx={{ whiteSpace: 'nowrap', fontSize: '11px' }}>
                {t('approvals.driver_pay.settlements.new_freight_rate')}
              </Label>
              <TextField
                size="small"
                variant="outlined"
                sx={{ width: 150 }}
                value={newFreightRate}
                onChange={(e) => setNewFreightRate(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: '$',
                    style: { maxHeight: '30px' },
                  },
                  htmlInput: {
                    style: { textAlign: 'right' },
                    type: 'number',
                    step: '0.01',
                    min: '0',
                  },
                }}
                disabled={isApplyingRate}
              />
            </Box>
            <SmallLoadingButton
              variant="contained"
              color="success"
              sx={{ alignSelf: 'flex-end' }}
              onClick={handleApplyRate}
              loading={isApplyingRate}
              disabled={!newFreightRate || isApplyingRate}
            >
              {t('approvals.driver_pay.settlements.apply_rate', 'Apply Rate')}
            </SmallLoadingButton>
            <Divider orientation="vertical" flexItem />
            <SmallButton
              variant="outlined"
              color="secondary"
              startIcon={<Add />}
              sx={{ alignSelf: 'flex-end' }}
              onClick={handleAddAddOnClick}
              disabled={!canEditSettlement || isUpdatingSettlementAddOnCharges}
            >
              {t('approvals.driver_pay.settlements.add_on')}
            </SmallButton>
          </Box>
        </Box>
      </form>
      {/* Settlement Details Table and Pricing */}
      <FormProvider {...formMethods}>
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Box
            sx={(theme) => ({
              ...gridConfig,
              gridTemplateRows: '36px',
              bgcolor: theme.semantic.surfaceSunken,
            })}
          >
            {headerColumns.map((header) => (
              <Typography
                key={header}
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                {header}
              </Typography>
            ))}
          </Box>

          {isLoadingAddOnArea ? (
            <LoadingPlaceholder />
          ) : (
            <>
              <RebillSettlementLineItems
                lineItems={lineItems}
                settlement={settlement}
                currencyFormatter={currencyFormatter}
                gridConfig={gridConfig}
              />
              <RebillSettlementAddOnCharges
                settlementId={settlementId}
                settlementAddOnCharges={settlementAddOnCharges}
                currencyFormatter={currencyFormatter}
                isEditing={isEditing}
                ref={addOnChargesRef}
                gridConfig={gridConfig}
              />
            </>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            bgcolor: 'background.paper',
            py: 1.5,
            px: 1,
          }}
        >
          {isEditing ? (
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                onSubmitAddOns();
              }}
              sx={{
                display: 'flex',
                gap: 1,
              }}
            >
              <SmallButton
                type="button"
                color="inherit"
                variant="contained"
                startIcon={<CloseIcon />}
                onClick={() => {
                  setIsEditing(false);
                  reset();
                }}
                sx={{ fontWeight: 500, bgcolor: 'background.paper' }}
              >
                {t('approvals.driver_pay.settlements.cancel')}
              </SmallButton>
              <SmallLoadingButton
                type="submit"
                color="success"
                loading={isUpdatingSettlementAddOnCharges}
                disabled={!isReadyForSubmission}
                startIcon={<CheckIcon />}
              >
                {t('approvals.driver_pay.settlements.save')}
              </SmallLoadingButton>
            </Box>
          ) : (
            <SmallButton
              color="brandV2Yellow"
              onClick={() => setIsEditing(true)}
              startIcon={<EditOutlinedIcon />}
              disabled={!canEditSettlement}
            >
              {t('approvals.driver_pay.settlements.edit_add_ons')}
            </SmallButton>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
          }}
        >
          <RebillSettlementPricing settlement={settlement} />
        </Box>
      </FormProvider>
    </Box>
  );
});
